import { SearchSmall } from '@zoopla-eng/zds/icons';
import { fontGroups } from '@zoopla-eng/zds/themes';
import {
    SearchContext,
    SearchPageType,
    SearchType
} from '@zoopla/listing-search-context';
import { useSearchLocation } from '@zoopla/listing-search/use-search-location';
import {
    bedsTitleSelector,
    durationSelector,
    isTravelTimeSearchSelector,
    priceTitleSelector,
    propertyTypeSelector,
    radiusSelector,
    transportTypeSelector
} from '@zoopla/redux-reducer-filters';
import { FC, useContext } from 'react';
import { useSelector } from 'react-redux';
import {
    dividerStyle,
    mobileQuickSearchBarWrapperStyle,
    styledLocationTextStyle,
    styledRadiusTextStyle,
    textStyle,
    wrapperStyle
} from './MobileQuickSearchButton.styles.css';
import { trackQuickSearchButtonClick } from './MobileQuickSearchButton.tracking';

type ModalTypes = 'search_initiation' | null;

const useSearchType = (): SearchPageType => {
    const {
        state: { searchType }
    } = useContext(SearchContext);

    return searchType;
};

const useSelectPriceTitle = () => {
    const searchType = useSearchType();
    const { forSale, toRent } = useSelector(priceTitleSelector);

    return searchType === SearchType.ForSale ? forSale : toRent;
};

const useSelectBedsTitle = () => useSelector(bedsTitleSelector);

const durationOptionsText: { [key: string]: string } = {
    '900': '15 minutes',
    '1800': '30 minutes',
    '2700': '45 minutes',
    '3600': '1 hour',
    '4500': '1 ¼ hour'
};
const transportTypeOptionsText: { [key: string]: string } = {
    driving: 'Driving',
    walking: 'Walking',
    walking_train: 'Train',
    public_transport: 'Public transport',
    cycling: 'Cycling'
};

export type Props = {
    openModal: (type: ModalTypes) => void;
};

export const MobileQuickSearchButton: FC<React.PropsWithChildren<Props>> = ({
    openModal
}) => {
    const { selectedValue: radiusSelectedValue, options: radiusOptions } =
        useSelector(radiusSelector);

    const {
        selectedValue: propertySelectedValue,
        options: propertyTypeOptions
    } = useSelector(propertyTypeSelector);

    const radiusLabel = radiusOptions.find((option) => {
        if (radiusSelectedValue !== '') {
            return option.value === radiusSelectedValue;
        }
        return radiusOptions[0];
    })?.label;

    const propertyTypeLabel = propertyTypeOptions.find(
        (option) => option.value === propertySelectedValue
    )?.label;

    const locationTextValue = useSearchLocation();

    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);

    const selectBedsTitle = useSelectBedsTitle();
    const selectPriceTitle = useSelectPriceTitle();

    const { selectedValue: durationValue, options: durationOptions } =
        useSelector(durationSelector);

    const {
        selectedValue: transportTypeSelectedValue,
        options: transportTypeOptions
    } = useSelector(transportTypeSelector);

    const durationTitleDisplay =
        durationOptionsText[durationValue || durationOptions[0].value];

    const travelTypeDurationType =
        transportTypeOptionsText[
            transportTypeSelectedValue || transportTypeOptions[0].value
        ];
    const title = `${durationTitleDisplay} • ${travelTypeDurationType}`;

    // TODO: P5 - button?
    return (
        <div
            className={wrapperStyle}
            onClick={() => {
                openModal('search_initiation');
                trackQuickSearchButtonClick();
            }}
            data-testid="testid_quick_search_modal_button"
        >
            <div className={mobileQuickSearchBarWrapperStyle}>
                <div>
                    <SearchSmall accessibilityType="decorative" />
                </div>
                <p className={`${textStyle} ${fontGroups.ladle}`}>
                    <span
                        className={styledLocationTextStyle}
                        color="text-title"
                    >
                        {locationTextValue}
                    </span>
                    <span className={styledRadiusTextStyle}>
                        <span className={dividerStyle} />
                        {!isTravelTimeSearch ? `${radiusLabel}` : `${title}`}
                    </span>
                    <span className={dividerStyle} />
                    {selectBedsTitle}
                    <span className={dividerStyle} />
                    {selectPriceTitle}
                    <span className={dividerStyle} />
                    {propertyTypeLabel}
                </p>
            </div>
        </div>
    );
};
